import React, { useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WORD_APP_PATH, HUB_PATH, PROFILE_PATH } from '../config';
import { WORD_APP } from '../apps';
import { pickPubToast } from '../content/pubToasts';
import BrandMark from './BrandMark';
import SiteFooter from './SiteFooter';
import { adminEntryPath, rememberAdminExit } from '../utils/adminNav';
import { profilePath, rememberProfileExit } from '../utils/profileNav';
import './AppShell.css';

function AppShell({
  title,
  children,
  app = WORD_APP,
  showBack = false,
  backTo = HUB_PATH,
  hideTabs = false,
  adminMode = false
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = Boolean(user?.isAdmin);
  const ownProfilePath = user?.username
    ? profilePath(user.username, app?.key)
    : `${WORD_APP_PATH}/game`;
  const [brandToast, setBrandToast] = useState('');
  const [brandCheers, setBrandCheers] = useState(false);
  const toastTimerRef = useRef(null);

  const onHub = location.pathname === HUB_PATH;

  const handleBrandClick = () => {
    if (!onHub) {
      navigate(HUB_PATH);
      return;
    }
    setBrandToast(pickPubToast());
    setBrandCheers(true);
    window.setTimeout(() => setBrandCheers(false), 700);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => setBrandToast(''), 3200);
  };

  return (
    <div className={`app-shell${hideTabs ? ' app-shell--no-tabs' : ''}`}>
      <header className="app-topbar">
        <div className="app-topbar-left">
          {showBack ? (
            <button
              type="button"
              className="app-icon-button"
              onClick={() => navigate(backTo)}
              aria-label="Назад"
            >
              ←
            </button>
          ) : null}
          <div className="app-brand">
            <BrandMark
              size="sm"
              cheers={brandCheers}
              onClick={handleBrandClick}
              title={onHub ? 'Тост' : 'На главную'}
            />
            {app?.title ? <span className="app-brand-text">{app.title}</span> : null}
          </div>
        </div>
        <div className="app-topbar-center">
          {title ? <h1 className="app-page-title">{title}</h1> : null}
        </div>
        <div className="app-topbar-right">
          <span className="app-username">{user?.username}</span>
          {isAdmin && !adminMode && (
            <button
              type="button"
              className="app-chip admin"
              onClick={() => {
                rememberAdminExit(location.pathname);
                navigate(adminEntryPath(app?.key));
              }}
            >
              Админ
            </button>
          )}
          <button
            type="button"
            className={`app-icon-button app-profile-button${
              location.pathname.startsWith(PROFILE_PATH) ||
              location.pathname.startsWith(`${WORD_APP_PATH}/profile`)
                ? ' app-profile-button--active'
                : ''
            }`}
            onClick={() => {
              rememberProfileExit(location.pathname);
              navigate(ownProfilePath);
            }}
            aria-label="Профиль"
            title="Профиль"
          >
            <svg
              className="app-profile-icon"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M5 19.5c1.8-3.2 4.2-4.8 7-4.8s5.2 1.6 7 4.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {brandToast ? (
        <div className="app-brand-toast" role="status">
          {brandToast}
        </div>
      ) : null}

      <main className="app-main">{children}</main>

      <SiteFooter className="site-footer--shell" rules={app?.footerRules || []} />

      {!hideTabs && app?.tabs?.length ? (
        <nav
          className="app-tabbar"
          aria-label="Основная навигация"
          style={{ gridTemplateColumns: `repeat(${app.tabs.length}, 1fr)` }}
        >
          {app.tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}
              title={tab.title}
            >
              <span className="app-tab-label">{tab.label}</span>
            </NavLink>
          ))}
        </nav>
      ) : null}
    </div>
  );
}

export default AppShell;
